import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  useToast,
  Spinner,
  Text,
  Flex,
  Badge,
} from '@chakra-ui/react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { auth } from '../../utils/firebaseConfig';

interface Comment {
  id: string;
  postId: string;
  userId: string;
  content: string;
  timestamp?: any;
  taggedUsers?: Array<{ id: string; firstName: string; lastName: string }>;
}

function CommentsManagement() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const fetchComments = async () => {
    const authToken = await auth.currentUser?.getIdToken();
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}admin/comments`,
      {
        headers: {
          Authorization: authToken,
          Accept: 'application/json',
        },
      }
    );
    return response.data;
  };

  const { data: comments, isLoading, error } = useQuery<Comment[]>({
    queryKey: ['admin-comments'],
    queryFn: fetchComments,
  });

  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      const authToken = await auth.currentUser?.getIdToken();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}admin/comments/${commentId}`,
        {
          headers: {
            Authorization: authToken,
            'Content-Type': 'application/json',
          },
        }
      );

      toast({
        title: 'Comment deleted',
        description: 'Comment has been deleted successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });

      queryClient.invalidateQueries({ queryKey: ['admin-comments'] });
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete comment.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString();
    }
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    }
    return new Date(timestamp).toLocaleString();
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="200px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Text color="red.500">Error loading comments. Please try again.</Text>
      </Box>
    );
  }

  return (
    <Box overflowX="auto">
      <Table variant="simple" colorScheme="gray">
        <Thead>
          <Tr>
            <Th>Comment ID</Th>
            <Th>Post ID</Th>
            <Th>Content</Th>
            <Th>Tagged Users</Th>
            <Th>Created</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {comments && comments.length > 0 ? (
            comments.map((comment) => (
              <Tr key={comment.id}>
                <Td>
                  <Text fontSize="xs" fontFamily="mono">
                    {comment.id.substring(0, 8)}...
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="xs" fontFamily="mono">
                    {comment.postId.substring(0, 8)}...
                  </Text>
                </Td>
                <Td maxW="400px">
                  <Text noOfLines={3}>{comment.content}</Text>
                </Td>
                <Td>
                  {comment.taggedUsers && comment.taggedUsers.length > 0 ? (
                    <Badge colorScheme="blue">
                      {comment.taggedUsers.length} user(s)
                    </Badge>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      None
                    </Text>
                  )}
                </Td>
                <Td>
                  <Text fontSize="sm">{formatDate(comment.timestamp)}</Text>
                </Td>
                <Td>
                  <Button
                    colorScheme="red"
                    size="sm"
                    onClick={() => handleDeleteComment(comment.id)}
                  >
                    Delete
                  </Button>
                </Td>
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan={6} textAlign="center">
                <Text>No comments found</Text>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  );
}

export default CommentsManagement;

